/**
 * MiniMax H3 prompt library — weighted toward editing, not generation.
 *
 * H3 ranks #1 on Artificial Analysis' Video Editing leaderboard while sitting
 * #2/#3 on text- and image-to-video, so the reason to reach for it is almost
 * always "I already have a cut and I want to change one thing about it."
 * That is the half of the model nobody has written good prompts for yet, and
 * it is the half that maps onto ad work: one shoot, many variants.
 *
 * Every editing prompt follows MiniMax's documented shape:
 *   "In Video 1, <change>. Keep <preservation list>. Sound: <audio>."
 * The `Keep` clause is not decoration — naming what must not move is what stops
 * H3 re-diffusing the whole frame, and it is the single biggest difference
 * between a usable edit and a reroll.
 *
 * Sample clips referenced here are our own H3 renders, generated by
 * scripts/marketing/generate-minimax-h3-samples.mjs and served from the
 * marketing CDN. `sourceSlug` pairs an edit with the clip it was run against
 * so the page can show a real before/after.
 */

export type MinimaxH3Mode = 'edit' | 'reference' | 'generate'

export type MinimaxH3Category =
  | 'vfx'
  | 'relight'
  | 'background'
  | 'object'
  | 'wardrobe'
  | 'voice'
  | 'text'
  | 'camera'
  | 'style'
  | 'shot'

export type MinimaxH3AspectRatio = '16:9' | '9:16' | '1:1' | '4:3' | '21:9'

export interface MinimaxH3Prompt {
  /** url-safe id, also the sample-clip filename stem */
  slug: string
  /** 3-7 words, used as the card title */
  title: string
  mode: MinimaxH3Mode
  category: MinimaxH3Category
  /** the copy-ready prompt */
  prompt: string
  /** what you upload alongside the prompt */
  inputs: string
  /** 1-line ad-use angle */
  useCase: string
  tags: string[]
  aspectRatio: MinimaxH3AspectRatio
  /** slug of the unedited clip this was run against, for before/after display */
  sourceSlug?: string
  /** marks entries that ship with a rendered sample clip on the CDN */
  hasSample?: boolean
}

/** Human labels for the category filter. */
export const MINIMAX_H3_CATEGORY_LABELS: Record<MinimaxH3Category, string> = {
  vfx: 'VFX & Effects',
  relight: 'Relighting',
  background: 'Background Swap',
  object: 'Object Edit',
  wardrobe: 'Character & Wardrobe',
  voice: 'Voice & Dialogue',
  text: 'On-Screen Text',
  camera: 'Camera & Viewpoint',
  style: 'Style Transfer',
  shot: 'Cold Generation',
}

export const MINIMAX_H3_MODE_LABELS: Record<MinimaxH3Mode, string> = {
  edit: 'Edit a clip',
  reference: 'Reference-driven',
  generate: 'Text to video',
}

/** Sample clips live under this prefix; `${slug}.mp4`. */
export const MINIMAX_H3_CDN = 'https://cdn.soku.ai/marketing/videos/tools/minimax-h3'

export const MINIMAX_H3_PROMPTS: MinimaxH3Prompt[] = [
  // ───────────────────────────────────────────────────────────────────────
  // VFX & EFFECTS — the "add a thing to a finished cut" bucket
  // ───────────────────────────────────────────────────────────────────────
  {
    slug: 'edit-add-vfx',
    title: 'Add Light Particles to a Product Turn',
    mode: 'edit',
    category: 'vfx',
    prompt:
      'In Video 1, add a soft ring of golden light particles that lifts off the bottle and drifts upward as the hand turns it, plus a faint lens bloom on the glass edge. Keep the bottle, the cup, the hand movement, the lighting, and the camera handling exactly the same. Sound: quiet café ambience with a soft shimmer as the particles rise.',
    inputs: '1 source clip (2–15s)',
    useCase: 'Dress up a plain product cutdown without a compositor.',
    tags: ['particles', 'bloom', 'product', 'beauty'],
    aspectRatio: '16:9',
    sourceSlug: 'src-cafe-serum',
    hasSample: true,
  },
  {
    slug: 'vfx-impact-freeze',
    title: 'Freeze-Frame Impact Burst',
    mode: 'edit',
    category: 'vfx',
    prompt:
      'In Video 1, at the moment the subject plants their foot, add a single-frame white flash and a radial dust burst that expands outward from the contact point and settles over the next second. Keep the subject, their motion, the wardrobe, the background, and the camera move exactly the same. Sound: a low impact thud under the existing audio.',
    inputs: '1 source clip with a clear action beat',
    useCase: 'Sports and apparel hooks — punch up the first 2 seconds.',
    tags: ['impact', 'dust', 'hook', 'sports'],
    aspectRatio: '9:16',
  },
  {
    slug: 'vfx-screen-glow',
    title: 'Make a Device Screen Light the Scene',
    mode: 'edit',
    category: 'vfx',
    prompt:
      'In Video 1, turn on the phone screen so it emits a cool blue glow that spills onto the hands and the underside of the face, with a soft falloff on the surrounding surfaces. Keep the phone, the hands, the pose, the room, and the camera framing exactly the same. Sound: unchanged.',
    inputs: '1 source clip featuring a device',
    useCase: 'App install ads — imply the product is running without a screen recording.',
    tags: ['glow', 'device', 'app', 'relight'],
    aspectRatio: '9:16',
  },
  {
    slug: 'vfx-weather-overlay',
    title: 'Add Rain and Wet Ground',
    mode: 'edit',
    category: 'vfx',
    prompt:
      'In Video 1, add steady rain falling through the frame, wet reflective ground with small splashes, and damp highlights on the subject\'s shoulders. Keep the subject, their movement, their wardrobe, the buildings, and the camera path exactly the same. Sound: rain on pavement layered under the existing audio.',
    inputs: '1 source clip shot outdoors',
    useCase: 'Seasonal variants of one shoot — sell rain gear from dry footage.',
    tags: ['rain', 'weather', 'seasonal', 'atmosphere'],
    aspectRatio: '16:9',
  },
  {
    slug: 'vfx-slowmo-trail',
    title: 'Motion Trails on a Moving Subject',
    mode: 'edit',
    category: 'vfx',
    prompt:
      'In Video 1, add soft luminous motion trails that follow the subject\'s hands and trail off after roughly half a second, tinted to match the key light. Keep the subject, the speed of their motion, the wardrobe, the background, and the camera exactly the same. Sound: unchanged.',
    inputs: '1 source clip with visible hand or body motion',
    useCase: 'Energy-drink and fitness creative — make ordinary motion feel kinetic.',
    tags: ['trails', 'motion', 'fitness', 'stylize'],
    aspectRatio: '9:16',
  },

  // ───────────────────────────────────────────────────────────────────────
  // RELIGHTING
  // ───────────────────────────────────────────────────────────────────────
  {
    slug: 'edit-relight-night',
    title: 'Daylight to Warm Evening',
    mode: 'edit',
    category: 'relight',
    prompt:
      'In Video 1, change the lighting from bright overcast daylight to warm evening: a deep blue window outside, a low amber lamp raking across the table, and soft warm falloff on the bottle. Keep the bottle, the cup, the hand movement, the table, and the camera handling exactly the same. Sound: quiet café ambience with faint evening street noise.',
    inputs: '1 source clip (2–15s)',
    useCase: 'Two dayparts from one shoot — test morning vs evening creative.',
    tags: ['day-to-night', 'lighting', 'daypart', 'product'],
    aspectRatio: '16:9',
    sourceSlug: 'src-cafe-serum',
    hasSample: true,
  },
  {
    slug: 'relight-golden-hour',
    title: 'Flat Light to Golden Hour',
    mode: 'edit',
    category: 'relight',
    prompt:
      'In Video 1, relight the scene as low golden-hour sun from camera-left: long warm highlights across the subject, a soft orange rim on the shoulder and hair, and long shadows stretching to the right. Keep the subject, their movement, the wardrobe, the set dressing, and the camera framing exactly the same. Sound: unchanged.',
    inputs: '1 source clip lit flat or overcast',
    useCase: 'Rescue a grey-day shoot without a reshoot.',
    tags: ['golden-hour', 'lighting', 'rescue', 'warm'],
    aspectRatio: '16:9',
  },
  {
    slug: 'relight-neon-club',
    title: 'Neutral to Neon Nightlife',
    mode: 'edit',
    category: 'relight',
    prompt:
      'In Video 1, relight the scene with saturated nightlife colour: magenta key from the left, cyan rim from behind, and a slow pulse of the magenta in time with the beat. Keep the subject, their movement, the wardrobe, the room layout, and the camera exactly the same. Sound: a muffled club track behind the existing audio.',
    inputs: '1 source clip lit neutrally',
    useCase: 'Nightlife and beverage angles from a daytime studio shoot.',
    tags: ['neon', 'nightlife', 'colour', 'beverage'],
    aspectRatio: '9:16',
  },

  // ───────────────────────────────────────────────────────────────────────
  // BACKGROUND
  // ───────────────────────────────────────────────────────────────────────
  {
    slug: 'edit-swap-background',
    title: 'Studio Backdrop to City Rooftop',
    mode: 'edit',
    category: 'background',
    prompt:
      'In Video 1, replace the plain grey studio backdrop with an out-of-focus city rooftop at golden hour — warm sky, soft skyline bokeh — and match the light on her with a warm key from the right. Keep the woman, her sweatshirt, her expression, her nod, and the framing exactly the same. Sound: soft rooftop wind and distant traffic.',
    inputs: '1 source clip on a plain backdrop',
    useCase: 'One talent shoot, one environment per market.',
    tags: ['backdrop', 'localization', 'ugc', 'talent'],
    aspectRatio: '9:16',
    sourceSlug: 'src-studio-model',
    hasSample: true,
  },
  {
    slug: 'background-green-screen',
    title: 'Green Screen to Real Room',
    mode: 'edit',
    category: 'background',
    prompt:
      'In Video 1, replace the green screen behind the presenter with a warm modern living room — a sofa, a floor lamp, and a plant, all softly out of focus — and add a matching warm bounce on the presenter\'s left side. Keep the presenter, their gestures, their wardrobe, their eyeline, and the framing exactly the same. Sound: unchanged.',
    inputs: '1 green-screen clip',
    useCase: 'Skip keying and set dressing on talking-head UGC.',
    tags: ['green-screen', 'keying', 'presenter', 'ugc'],
    aspectRatio: '9:16',
  },
  {
    slug: 'background-window-view',
    title: 'Swap the View Through a Window',
    mode: 'edit',
    category: 'background',
    prompt:
      'In Video 1, change the view through the window from an overcast street to a snowy mountain ridge under clear blue sky, and cool the light falling through the window onto the interior to match. Keep the room, the furniture, the subject, their movement, and the camera exactly the same. Sound: unchanged.',
    inputs: '1 source clip with a window in frame',
    useCase: 'Travel and seasonal campaigns from a single interior setup.',
    tags: ['window', 'seasonal', 'travel', 'interior'],
    aspectRatio: '16:9',
  },

  // ───────────────────────────────────────────────────────────────────────
  // OBJECT EDIT
  // ───────────────────────────────────────────────────────────────────────
  {
    slug: 'edit-restyle-colorway',
    title: 'Change a Product Colourway',
    mode: 'edit',
    category: 'object',
    prompt:
      'In Video 1, change the sneaker from white to deep forest green with a cream midsole, keeping the same materials, stitching, and wear. Keep the pedestal, the shadow, the lighting, and the camera orbit exactly the same. Sound: low studio hum.',
    inputs: '1 source clip of the product',
    useCase: 'One product shoot, every SKU colourway.',
    tags: ['colourway', 'sku', 'ecommerce', 'product'],
    aspectRatio: '1:1',
    sourceSlug: 'src-sneaker-pedestal',
    hasSample: true,
  },
  {
    slug: 'edit-remove-object',
    title: 'Remove an Object from the Frame',
    mode: 'edit',
    category: 'object',
    prompt:
      'In Video 1, remove the concrete pedestal so the sneaker appears to float, and replace the hard contact shadow with a soft diffuse shadow on the floor below it. Keep the sneaker, its colour and materials, the lighting, and the camera orbit exactly the same. Sound: low studio hum.',
    inputs: '1 source clip',
    useCase: 'Clean a distracting prop out of otherwise usable footage.',
    tags: ['removal', 'cleanup', 'product', 'float'],
    aspectRatio: '1:1',
    sourceSlug: 'src-sneaker-pedestal',
    hasSample: true,
  },
  {
    slug: 'edit-add-element',
    title: 'Add a Product to an Existing Shot',
    mode: 'edit',
    category: 'object',
    prompt:
      'In Video 1, add a matte-white reusable water bottle held in her right hand, resting naturally against her side, lit by the same key light and casting a soft shadow on her sweatshirt. Keep the woman, her sweatshirt, her expression, her nod, the backdrop, and the framing exactly the same. Sound: quiet studio room tone.',
    inputs: '1 source clip',
    useCase: 'Insert a product into lifestyle footage shot without it.',
    tags: ['insertion', 'product', 'lifestyle', 'ugc'],
    aspectRatio: '9:16',
    sourceSlug: 'src-studio-model',
    hasSample: true,
  },
  {
    slug: 'object-swap-hero',
    title: 'Swap the Hero Product',
    mode: 'edit',
    category: 'object',
    prompt:
      'In Video 1, replace the cardboard coffee cup in the subject\'s hand with a slim matte-black insulated tumbler of the same height, held the same way, catching the same key light. Keep the subject, their grip, their movement, the background, and the camera exactly the same. Sound: unchanged.',
    inputs: '1 source clip',
    useCase: 'Re-cut a proven creative for a different product line.',
    tags: ['swap', 'product', 'reuse', 'winner'],
    aspectRatio: '9:16',
  },
  {
    slug: 'object-multiply-sku',
    title: 'Fill a Shelf with Variants',
    mode: 'reference',
    category: 'object',
    prompt:
      'In Video 1, extend the shelf behind the subject so it holds six of the product from Image 1 in a neat row, each lit by the same overhead light and slightly out of focus. Keep the subject, their movement, the foreground, the lighting, and the camera exactly the same. Sound: unchanged.',
    inputs: '1 source clip + 1 product image',
    useCase: 'Retail-shelf social proof without building a set.',
    tags: ['shelf', 'retail', 'reference-image', 'proof'],
    aspectRatio: '16:9',
  },

  // ───────────────────────────────────────────────────────────────────────
  // CHARACTER & WARDROBE
  // ───────────────────────────────────────────────────────────────────────
  {
    slug: 'wardrobe-swap-outfit',
    title: 'Change the Outfit, Keep the Take',
    mode: 'edit',
    category: 'wardrobe',
    prompt:
      'In Video 1, change the subject\'s plain grey sweatshirt to a structured cream linen blazer over a white tee, with fabric that moves naturally with their gestures. Keep the subject\'s face, hair, expression, gestures, the background, the lighting, and the camera exactly the same. Sound: unchanged.',
    inputs: '1 source clip',
    useCase: 'Test casual vs premium positioning on the same performance.',
    tags: ['wardrobe', 'apparel', 'positioning', 'variant'],
    aspectRatio: '9:16',
  },
  {
    slug: 'wardrobe-consistent-character',
    title: 'Hold One Character Across Shots',
    mode: 'reference',
    category: 'wardrobe',
    prompt:
      'Use the person in Image 1 as the subject and the camera movement in Video 1 as the shot. She walks into a bright open-plan office, sets a laptop bag on a desk, and turns to camera with a small smile. Match her face, hair, and build to Image 1 exactly. Sound: quiet office ambience and a bag settling on a desk.',
    inputs: '1–9 character images + 1 motion reference clip',
    useCase: 'A recurring brand spokesperson across a whole campaign.',
    tags: ['consistency', 'character', 'spokesperson', 'campaign'],
    aspectRatio: '16:9',
  },
  {
    slug: 'wardrobe-age-range',
    title: 'Re-Cast the Same Script',
    mode: 'reference',
    category: 'wardrobe',
    prompt:
      'Regenerate Video 1 with the person from Image 1 performing the same actions, in the same room, with the same framing and the same camera move. Match their face and build to Image 1. Keep the set, the lighting, the pacing, and the audio timing identical to Video 1. Sound: match Video 1.',
    inputs: '1 source clip + 1 talent image',
    useCase: 'Audience-matched variants — same script, different demographic.',
    tags: ['recast', 'audience', 'testing', 'talent'],
    aspectRatio: '9:16',
  },

  // ───────────────────────────────────────────────────────────────────────
  // VOICE & DIALOGUE
  // ───────────────────────────────────────────────────────────────────────
  {
    slug: 'voice-relocalize',
    title: 'Re-Voice a Clip in Another Language',
    mode: 'edit',
    category: 'voice',
    prompt:
      'In Video 1, replace the spoken dialogue with the same message delivered in natural conversational Spanish, matching the speaker\'s pacing, energy, and mouth movement. Keep the speaker, their expression, their gestures, the room, and the camera exactly the same. Sound: the new Spanish dialogue over the original room tone.',
    inputs: '1 source clip with speech',
    useCase: 'Ship every market from one talking-head shoot.',
    tags: ['localization', 'dubbing', 'lipsync', 'markets'],
    aspectRatio: '9:16',
  },
  {
    slug: 'voice-clone-transfer',
    title: 'Transfer a Voice onto New Delivery',
    mode: 'reference',
    category: 'voice',
    prompt:
      'Use the voice in Audio 1 and the speaker in Video 1. Have the speaker deliver this line in that voice, with matching mouth movement and a warm, unhurried read: "We rebuilt it from scratch — and it shows." Keep the speaker\'s appearance, the room, the lighting, and the camera exactly the same. Sound: the cloned voice over the original room tone.',
    inputs: '1 source clip + 1 voice reference (2–15s)',
    useCase: 'A consistent brand voice across creative you did not record.',
    tags: ['voice-clone', 'brand-voice', 'audio-reference', 'vo'],
    aspectRatio: '16:9',
  },
  {
    slug: 'voice-tone-shift',
    title: 'Change the Read, Not the Take',
    mode: 'edit',
    category: 'voice',
    prompt:
      'In Video 1, re-deliver the same dialogue with a calmer, lower-energy read — slower pacing, softer emphasis — and match the speaker\'s mouth movement to the new timing. Keep the speaker, their wardrobe, the room, the lighting, and the camera exactly the same. Sound: the new read over the original room tone.',
    inputs: '1 source clip with speech',
    useCase: 'A/B the delivery when the visual is already a winner.',
    tags: ['delivery', 'ab-test', 'read', 'vo'],
    aspectRatio: '9:16',
  },

  // ───────────────────────────────────────────────────────────────────────
  // ON-SCREEN TEXT
  // ───────────────────────────────────────────────────────────────────────
  {
    slug: 'text-kinetic-headline',
    title: 'Kinetic Headline Behind the Subject',
    mode: 'edit',
    category: 'text',
    prompt:
      'In Video 1, add a large bold sans-serif headline reading "BUILT TO LAST" floating in the space behind the subject, sitting behind them in depth so they always occlude it, tracking with the camera move and never covering their face. Keep the subject, their movement, the background, the lighting, and the camera exactly the same. Sound: a soft whoosh as the text settles.',
    inputs: '1 source clip',
    useCase: 'Title cards baked into the shot instead of added in post.',
    tags: ['typography', 'headline', 'occlusion', 'title'],
    aspectRatio: '16:9',
  },
  {
    slug: 'text-surface-projection',
    title: 'Project Text onto a Real Surface',
    mode: 'edit',
    category: 'text',
    prompt:
      'In Video 1, paint the words "OPEN LATE" onto the brick wall behind the subject so the letters follow the wall\'s perspective, curve over the mortar lines, and pick up the same shadows as the bricks. Keep the wall, the subject, their movement, the lighting, and the camera exactly the same. Sound: unchanged.',
    inputs: '1 source clip with a flat surface in frame',
    useCase: 'Location-specific copy without a location shoot.',
    tags: ['typography', 'perspective', 'surface', 'ooh'],
    aspectRatio: '16:9',
  },
  {
    slug: 'text-caption-package',
    title: 'Variety-Show Caption Package',
    mode: 'edit',
    category: 'text',
    prompt:
      'In Video 1, add bold outlined captions in the lower third that appear word by word in sync with the speech, in a punchy variety-show style with a thick dark outline and a small colour accent on the emphasised word. Keep the speaker, their delivery, the background, and the camera exactly the same. Sound: unchanged.',
    inputs: '1 source clip with speech',
    useCase: 'Sound-off feed performance on talking-head creative.',
    tags: ['captions', 'sound-off', 'social', 'lower-third'],
    aspectRatio: '9:16',
  },

  // ───────────────────────────────────────────────────────────────────────
  // CAMERA & VIEWPOINT
  // ───────────────────────────────────────────────────────────────────────
  {
    slug: 'camera-reframe-vertical',
    title: 'Reframe a Landscape Cut to Vertical',
    mode: 'edit',
    category: 'camera',
    prompt:
      'In Video 1, reframe the shot to a 9:16 vertical composition centred on the subject, extending the scene naturally above and below rather than cropping, and keeping the subject the same size in frame throughout. Keep the subject, their movement, the set, the lighting, and the pacing exactly the same. Sound: unchanged.',
    inputs: '1 landscape source clip',
    useCase: 'One master cut, every placement — Reels, Shorts, feed.',
    tags: ['reframe', 'vertical', 'placement', 'aspect'],
    aspectRatio: '9:16',
  },
  {
    slug: 'camera-push-in',
    title: 'Add a Camera Move to a Locked Shot',
    mode: 'edit',
    category: 'camera',
    prompt:
      'In Video 1, add a slow steady push-in toward the subject over the full clip, ending roughly 20 percent tighter, with the parallax you would get from a real dolly. Keep the subject, their movement, the set, the lighting, and the audio timing exactly the same. Sound: unchanged.',
    inputs: '1 locked-off source clip',
    useCase: 'Give static product footage the energy of a directed shot.',
    tags: ['dolly', 'push-in', 'parallax', 'movement'],
    aspectRatio: '16:9',
  },

  // ───────────────────────────────────────────────────────────────────────
  // STYLE TRANSFER
  // ───────────────────────────────────────────────────────────────────────
  {
    slug: 'style-match-reference',
    title: 'Apply a Reference Clip\'s Edit Style',
    mode: 'reference',
    category: 'style',
    prompt:
      'Take the subject and performance from Video 1 and apply the editing rhythm, transitions, colour grade, and on-screen graphic style of Video 2. Keep the subject\'s identity, wardrobe, and dialogue from Video 1 unchanged. Sound: keep Video 1\'s dialogue, adopt Video 2\'s music energy.',
    inputs: '2 source clips (your cut + the style reference)',
    useCase: 'Match a format that is already working in your feed.',
    tags: ['style-transfer', 'template', 'trend', 'format'],
    aspectRatio: '9:16',
  },
  {
    slug: 'style-film-stock',
    title: 'Regrade to a Film Look',
    mode: 'edit',
    category: 'style',
    prompt:
      'In Video 1, regrade the footage to warm 35mm film: lifted blacks, gentle halation on highlights, fine grain, and slightly desaturated greens. Keep the subject, their movement, the framing, the camera move, and the audio exactly the same. Sound: unchanged.',
    inputs: '1 source clip',
    useCase: 'A premium grade on footage shot on a phone.',
    tags: ['grade', 'film', 'halation', 'premium'],
    aspectRatio: '16:9',
  },

  // ───────────────────────────────────────────────────────────────────────
  // COLD GENERATION — what H3 makes with no footage at all
  // ───────────────────────────────────────────────────────────────────────
  {
    slug: 'gen-kitchen-pour',
    title: 'Cold-Brew Macro Pour',
    mode: 'generate',
    category: 'shot',
    prompt:
      'Macro shot: cold-brew coffee pours over clear ice in a tall glass on a walnut counter, condensation forming, morning side light through a blind casting soft stripes. The camera pushes in slowly as the pour finishes. Sound: ice cracking, liquid pouring, a quiet kitchen.',
    inputs: 'Text only',
    useCase: 'Appetite-appeal b-roll for food and beverage.',
    tags: ['macro', 'beverage', 'asmr', 'b-roll'],
    aspectRatio: '16:9',
    hasSample: true,
  },
  {
    slug: 'gen-street-walk',
    title: 'Rainy Neon Follow Shot',
    mode: 'generate',
    category: 'shot',
    prompt:
      'Vertical follow shot from behind: a man in a tan overcoat walks through a rainy neon-lit street at night, reflections on wet asphalt, the camera tracks him steadily at shoulder height, shallow depth of field. Cinematic colour, slight film grain. Sound: rain on pavement, distant traffic, footsteps in puddles.',
    inputs: 'Text only',
    useCase: 'Mood-setting opener for a brand film.',
    tags: ['cinematic', 'neon', 'tracking', 'opener'],
    aspectRatio: '9:16',
    hasSample: true,
  },
  {
    slug: 'gen-claymation-fox',
    title: 'Stop-Motion Claymation Fox',
    mode: 'generate',
    category: 'shot',
    prompt:
      'Stop-motion claymation: a small orange fox with visible thumbprints in the clay pads across a felt meadow, ears twitching, then sits and looks up at a paper-cutout sun. Handmade set, slight frame judder like real stop-motion. Sound: soft felt rustle and a gentle wooden music-box melody.',
    inputs: 'Text only',
    useCase: 'Distinctive brand-mascot work that does not read as AI.',
    tags: ['claymation', 'stop-motion', 'mascot', 'craft'],
    aspectRatio: '16:9',
    hasSample: true,
  },
]

/** The unedited clips that the before/after pairs are built from. */
export const MINIMAX_H3_SOURCES: Record<
  string,
  { title: string; prompt: string; aspectRatio: MinimaxH3AspectRatio }
> = {
  'src-cafe-serum': {
    title: 'Café product turn',
    prompt:
      'Handheld product shot on a café table by a window: a frosted glass serum bottle beside a ceramic cup, bright overcast daylight from the left, a hand enters frame and slowly turns the bottle to face camera. Natural colour, shallow depth of field, shot like real phone footage for a skincare brand. Sound: quiet café ambience.',
    aspectRatio: '16:9',
  },
  'src-studio-model': {
    title: 'Studio talent nod',
    prompt:
      'Vertical studio shot: a young woman in a plain grey sweatshirt stands against a seamless light-grey backdrop, soft even key light, she looks to camera and gives a small confident nod. Clean commercial lighting, no props, framed for social. Sound: quiet studio room tone.',
    aspectRatio: '9:16',
  },
  'src-sneaker-pedestal': {
    title: 'Sneaker orbit',
    prompt:
      'Square product shot: a clean white running sneaker on a low concrete pedestal, slow orbit of the camera from left to right, cool neutral studio light, soft shadow under the sole. Crisp commercial product photography look. Sound: low studio hum.',
    aspectRatio: '1:1',
  },
}
